import { useEffect, useRef, useState } from 'react';
import { propertyCodesApi } from '../services/propertyCodesApi';
import { useAppDispatch } from '../store/store';
import { dedupeCodeSuggestions, EMPTY_PROPERTY_CODES } from './propertyCodes';
// Fetches and dedupes the corporate/promo codes for the current selection's
// properties, ready to prepopulate the search bar's code typeahead. Mirrors the
// offers fan-out: one cached `getPropertyCodes` call per property, settled so a
// single failing property never sinks the rest, then merged into one set. A run
// counter guards against a slow earlier fan-out overwriting a newer selection.
export const usePropertyCodes = (properties) => {
    const dispatch = useAppDispatch();
    const [codes, setCodes] = useState(EMPTY_PROPERTY_CODES);
    const runId = useRef(0);
    // The set of properties is the only input; key on their ids (order-agnostic)
    // so re-selecting the same market does not refetch.
    const propertyKey = [...properties.map(p => p.id)].sort().join(',');
    useEffect(() => {
        if (properties.length === 0) {
            setCodes(EMPTY_PROPERTY_CODES);
            return;
        }
        const thisRun = ++runId.current;
        const fetchOne = async (property) => {
            const subscription = dispatch(propertyCodesApi.endpoints.getPropertyCodes.initiate(property.id));
            try {
                return await subscription.unwrap();
            }
            catch (error) {
                // A property with no configured codes (or a transient failure) must not
                // block the typeahead — log at the IO boundary and contribute nothing.
                console.warn(`Sales Desk: property codes fetch failed for property ${property.id}`, error);
                return EMPTY_PROPERTY_CODES;
            }
            finally {
                // keepUnusedDataFor keeps the entry cached after we unsubscribe, so
                // re-selecting the property is served from the store.
                subscription.unsubscribe();
            }
        };
        void Promise.all(properties.map(fetchOne)).then(perProperty => {
            if (thisRun === runId.current) {
                setCodes(dedupeCodeSuggestions(perProperty));
            }
        });
        // propertyKey stands in for the properties array identity, so the effect
        // reruns only when the set of property ids actually changes.
    }, [propertyKey, dispatch]);
    return codes;
};
